/* Campaign vouchers — save on the product page, apply inside the side cart.
 *
 * Two interactions, deliberately different, following eMAG:
 *
 *   Product page  "Salvează voucher"  — there is no cart yet, so nothing can be
 *                                       applied. The code is remembered locally.
 *   Side cart     "Aplică voucher"    — applies to the live cart WITHOUT leaving
 *                                       the drawer.
 *
 * The side cart only offers vouchers the shopper actually saved; Liquid cannot
 * read localStorage, so every cart card is rendered `hidden` and revealed here.
 *
 * Applying is a fetch to /discount/<code>, which sets the code on the cart
 * session, followed by the drawer's own re-render. It used to be a link, which
 * navigated to /cart — a page this shop does not use.
 */
(function () {
  'use strict';
  if (window.__cdtVoucher) return;
  window.__cdtVoucher = 1;

  var STORE_KEY = 'cdt_saved_vouchers';
  var cart = null;

  function saved() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY) || '[]'); } catch (e) { return []; }
  }
  function save(code) {
    var list = saved();
    if (list.indexOf(code) === -1) list.push(code);
    try { localStorage.setItem(STORE_KEY, JSON.stringify(list)); } catch (e) {}
  }
  function isSaved(code) { return saved().indexOf(code) > -1; }

  function money(n) { return (n / 100).toFixed(2).replace(/\.00$/, '') + ' lei'; }

  function scopedIds(card) {
    return (card.getAttribute('data-product-ids') || '').split(',').filter(Boolean);
  }

  function qualifying(card, field) {
    if (!cart || !cart.items) return 0;
    if (card.getAttribute('data-scope') === 'all') {
      return cart.items.reduce(function (n, i) { return n + i[field]; }, 0);
    }
    var ids = scopedIds(card);
    if (!ids.length) return 0;
    return cart.items.reduce(function (n, i) {
      return ids.indexOf(String(i.product_id)) > -1 ? n + i[field] : n;
    }, 0);
  }

  /* Cart lines that count toward this campaign, in cart order. Same matching
     rule as qualifying(), returned as items so the slot row can show them. */
  function qualifyingItems(card) {
    if (!cart || !cart.items) return [];
    if (card.getAttribute('data-scope') === 'all') return cart.items.slice();
    var ids = scopedIds(card);
    if (!ids.length) return [];
    return cart.items.filter(function (i) {
      return ids.indexOf(String(i.product_id)) > -1;
    });
  }

  /* Slots only make sense for a COUNT of products. A min-subtotal campaign is
     measured in lei, where "2 of 3 boxes" would be a lie — that one keeps the
     sentence only. */
  function paintSlots(card, type, need, remaining, applied) {
    var box = card.querySelector('[data-cdt-voucher-slots]');
    if (!box) return;

    if (type !== 'min_quantity' || !need) { box.hidden = true; box.innerHTML = ''; return; }

    box.innerHTML = buildSlotsHTML(card, need);
    box.hidden = false;
    box.classList.toggle('is-ready', remaining === 0 || !!applied);
  }

  /* Shared by the cart card and the picker sheet, so the shopper sees the same
     "1 of 2 filled" picture in both places. */
  function buildSlotsHTML(card, need) {
    var items = qualifyingItems(card);
    var have = 0;
    var html = '';

    for (var i = 0; i < items.length && have < need; i++) {
      var it = items[i];
      /* A line of qty 3 fills three slots, matching how the discount counts. */
      for (var q = 0; q < it.quantity && have < need; q++) {
        /* Modern ?width= param, not the legacy `_80x` filename form — the
           latter did not resolve for these cart images (naturalWidth stayed 0).
           Same approach the campaign picker uses. */
        var src = it.image
          ? it.image + (it.image.indexOf('?') > -1 ? '&' : '?') + 'width=96'
          : '';
        html += '<span class="cdt-vch__slot is-filled" title="' +
                String(it.product_title || '').replace(/"/g, '&quot;') + '">' +
                /* NOT lazy: the card can sit below the drawer's fold, and a
                   lazy image that never enters the viewport never loads at all
                   — the same way it bit the variant tiles. These are 96px. */
                (src ? '<img src="' + src + '" alt="" decoding="async">' : '') +
                '<i class="cdt-vch__tick" aria-hidden="true">✓</i></span>';
        have++;
      }
    }
    for (var e = have; e < need; e++) {
      html += '<span class="cdt-vch__slot is-empty" aria-hidden="true">+</span>';
    }

    return html;
  }

  /* Reveal saved vouchers SYNCHRONOUSLY, with no network involved.
     
     The cart card is server-rendered `hidden` — Liquid cannot know whether this
     shopper saved the voucher, since that lives in localStorage. It was then
     un-hidden only by paintCart(), i.e. after a /cart.js fetch plus the 200ms
     observer debounce. Measured on a real cart update: the drawer re-rendered at
     323ms and the card reappeared at 1338ms, so it vanished and popped back in
     on every single cart interaction.

     localStorage is synchronous, so the reveal never needed to wait for
     anything. Only the progress line and slots need cart data, and those can
     keep arriving late without moving the card in and out of the layout.

     Writes only on a real difference, so triggering the observer from here
     settles after one pass instead of looping. */
  function revealSaved() {
    var cards = document.querySelectorAll('.cdt-vch-list--cart [data-cdt-voucher]');
    for (var i = 0; i < cards.length; i++) {
      var want = !isSaved(cards[i].getAttribute('data-code'));
      if (cards[i].hidden !== want) cards[i].hidden = want;
    }
  }

  /* ── product page: save ─────────────────────────────────────────────── */
  function paintSaveButtons() {
    var btns = document.querySelectorAll('[data-cdt-voucher-save]');
    for (var i = 0; i < btns.length; i++) {
      var card = btns[i].closest('[data-cdt-voucher]');
      if (!card) continue;
      var on = isSaved(card.getAttribute('data-code'));
      btns[i].textContent = on ? 'Voucher salvat' : 'Salvează voucher';
      btns[i].classList.toggle('is-saved', on);
      btns[i].disabled = on;
    }
  }

  /* ── side cart: reveal saved vouchers, show progress ────────────────── */
  function paintCart() {
    var cards = document.querySelectorAll('.cdt-vch-list--cart [data-cdt-voucher]');
    var anyVisible = false;

    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var code = card.getAttribute('data-code');

      if (!isSaved(code)) { card.hidden = true; continue; }
      card.hidden = false;
      anyVisible = true;

      var applied = cart && (cart.discount_codes || []).some(function (d) {
        return d.code === code && d.applicable;
      });
      card.classList.toggle('is-applied', !!applied);

      var btn = card.querySelector('[data-cdt-voucher-apply]');
      var out = card.querySelector('[data-cdt-voucher-progress]');
      var type = card.getAttribute('data-condition-type');
      var need = parseFloat(card.getAttribute('data-condition-value')) || 0;

      var remaining = 0;
      if (need && type !== 'none') {
        if (type === 'min_quantity') {
          remaining = Math.max(0, Math.round(need) - qualifying(card, 'quantity'));
        } else {
          remaining = Math.max(0, Math.round(need * 100) - qualifying(card, 'line_price'));
        }
      }

      paintSlots(card, type, Math.round(need), remaining, applied);

      if (out) {
        if (applied) {
          out.textContent = 'Voucher aplicat';
        } else if (!need || type === 'none') {
          out.hidden = true;
        } else if (remaining === 0) {
          out.textContent = 'Condiție îndeplinită — aplică voucherul';
        } else if (type === 'min_quantity') {
          out.textContent = 'Mai adaugă ' + remaining + (remaining === 1 ? ' produs' : ' produse') + ' din campanie';
        } else {
          out.textContent = 'Mai adaugă ' + money(remaining) + ' din campanie';
        }
        if (need && type !== 'none') out.hidden = false;
      }

      card.classList.toggle('is-ready', remaining === 0);
      if (btn) {
        btn.textContent = applied ? 'Aplicat' : 'Aplică voucher';
        btn.disabled = !!applied;
      }
    }

    var list = document.querySelector('.cdt-vch-list--cart');
    if (list) list.hidden = !anyVisible;
  }

  var cartInFlight = null, cartReadAt = 0, painting = false;

  /* Our own repaint writes to the DOM, which the drawer's MutationObserver sees
     as a cart change, which asks for the cart again, which repaints... The flag
     lets the observer ignore mutations it caused itself. Cleared on a macrotask
     because observer callbacks are delivered as microtasks, i.e. before this. */
  function paintAll() {
    painting = true;
    paintSaveButtons();
    paintCart();
    setTimeout(function () { painting = false; }, 0);
  }

  function readCart(force) {
    /* Coalesced. The re-render, the repaint and the observer all ask for the
       cart inside the same few hundred ms, so one voucher apply was firing FIVE
       /cart.js reads. One in flight at a time, and a cached answer is good
       enough for 500ms unless a caller knows the cart just changed. */
    if (cartInFlight) return cartInFlight;
    if (!force && Date.now() - cartReadAt < 500) { paintAll(); return Promise.resolve(); }

    cartInFlight = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) { if (c) cart = c; })
      .catch(function () {})
      .then(function () { cartReadAt = Date.now(); cartInFlight = null; paintAll(); });
    return cartInFlight;
  }

  function applyCode(card, btn) {
    var code = card.getAttribute('data-code');
    btn.disabled = true;
    btn.textContent = 'Se aplică…';

    /* Same-origin fetch carries the cart cookie, so the code lands on the
       session exactly as a visit to /discount/<code> would — minus the
       redirect that used to eject the shopper from the drawer.

       ?redirect=/cart.js is NOT cosmetic. /discount/<code> answers 302, and
       without a redirect target it lands on the storefront home page — so this
       fetch downloaded 762KB of HTML in 1.56s purely to discard it, for an
       operation whose entire job is to set a cookie. Measured on a real cart:
       762KB/1558ms -> 12KB/~550ms. On mobile data that difference IS the lag.

       Pointing it at /cart.js also RETURNS the updated cart, so the separate
       readCart() round trip disappears: 3 requests/822KB -> 2 requests/~60KB. */
    var fresh = null;
    return fetch('/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent('/cart.js'),
                 { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (body) {
        /* Defensive: if Shopify ever stops honouring the redirect we get HTML
           here, not JSON. Fall back to a plain read rather than paint nothing. */
        if (body) { try { fresh = JSON.parse(body); } catch (e) { fresh = null; } }
        if (fresh && fresh.items) cart = fresh;
        /* The drawer re-renders itself through the Section Rendering API, which
           is what makes the new total appear without a page load. */
        if (typeof window.cdxCartUpdated === 'function') return window.cdxCartUpdated();
        return null;
      })
      .then(function () {
        /* Repaint AFTER the re-render — it replaces the voucher cards. */
        if (fresh && fresh.items) { paintAll(); return null; }
        return readCart(true);
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = 'Aplică voucher';
      });
  }


  /* ------------------------------------------------------------------ *
   * Campaign product picker
   *
   * "Mai adaugă 2 produse din campanie" is a dead end on its own: nothing in
   * the cart tells the shopper WHICH products count. This opens a sheet over
   * the drawer listing them, with an add button on each, so the condition can
   * be met without leaving the cart. Modelled on eMAG's post-add-to-cart sheet
   * (rule at the top, product grid, "continuă cumpărăturile" at the bottom).
   *
   * Product data is fetched from /products/<handle>.js on OPEN, never read from
   * the campaign snapshot: the snapshot is rewritten only when a campaign is
   * deployed, so any price or stock baked into it would be stale. Handles are
   * the only thing it carries.
   * ------------------------------------------------------------------ */
  var PICK_BATCH = 12;
  var pickEl = null, pickCache = {};

  function money(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + ' lei';
  }

  function buildPicker() {
    if (pickEl) return pickEl;
    pickEl = document.createElement('div');
    pickEl.className = 'cdt-pick';
    pickEl.setAttribute('hidden', '');
    pickEl.innerHTML =
      '<div class="cdt-pick__overlay" data-pick-close></div>' +
      '<div class="cdt-pick__panel" role="dialog" aria-modal="true" aria-label="Produse din campanie">' +
        '<div class="cdt-pick__head">' +
          '<div class="cdt-pick__headings">' +
            '<span class="cdt-pick__label" data-pick-label></span>' +
            '<strong class="cdt-pick__name" data-pick-name></strong>' +
          '</div>' +
          '<button type="button" class="cdt-pick__x" data-pick-close aria-label="Închide">&times;</button>' +
        '</div>' +
        '<p class="cdt-pick__rule" data-pick-rule></p>' +
        '<div class="cdt-pick__prog" data-pick-prog hidden>' +
          '<div class="cdt-vch__slots cdt-pick__slots" data-pick-slots></div>' +
          '<p class="cdt-pick__need" data-pick-need></p>' +
        '</div>' +
        '<div class="cdt-pick__grid" data-pick-grid></div>' +
        '<div class="cdt-pick__foot">' +
          '<a class="cdt-pick__all" data-pick-all hidden>Vezi toate produsele</a>' +
          '<button type="button" class="cdt-pick__done" data-pick-close>Continuă cumpărăturile</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(pickEl);

    pickEl.addEventListener('click', function (e) {
      if (e.target.closest('[data-pick-close]')) { closePicker(); return; }
      var add = e.target.closest('[data-pick-add]');
      if (add) addFromPicker(add);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && pickEl && !pickEl.hasAttribute('hidden')) closePicker();
    });
    return pickEl;
  }

  function closePicker() {
    if (!pickEl) return;
    pickEl.setAttribute('hidden', '');
    document.body.classList.remove('cdt-pick-open');
  }

  function cardHTML(p, campName) {
    var v = (p.variants || [])[0] || {};
    var single = (p.variants || []).length === 1;
    var soldOut = !p.available;
    var cmp = v.compare_at_price && v.compare_at_price > v.price
      ? '<s>' + money(v.compare_at_price) + '</s>' : '';
    var img = p.featured_image
      ? '<img src="' + p.featured_image + '&width=220" alt="" loading="lazy" decoding="async">' : '';

    /* The campaign pill, same red and same bottom-left position as the ribbon on
       collection and search tiles. These cards are built client-side from
       /products/<handle>.js, which knows nothing about campaigns, so the badge
       has to be added here — without it the picker was the only campaign
       surface whose products carried no marking, and tapping through to the
       collection landed on tiles that suddenly had one. */
    var badge = campName
      ? '<span class="cdt-pick__badge">' + campName + '</span>' : '';

    /* One variant -> add straight to the cart. Several -> we cannot choose for
       them, so link to the product instead of guessing a variant. */
    var action = soldOut
      ? '<span class="cdt-pick__btn is-off">Stoc epuizat</span>'
      : single
        ? '<button type="button" class="cdt-pick__btn" data-pick-add="' + v.id + '">+ Adaugă</button>'
        : '<a class="cdt-pick__btn cdt-pick__btn--link" href="/products/' + p.handle + '">Alege opțiuni</a>';

    return '<article class="cdt-pick__card' + (soldOut ? ' is-off' : '') + '">' +
      '<a class="cdt-pick__img" href="/products/' + p.handle + '">' + img + badge + '</a>' +
      '<a class="cdt-pick__title" href="/products/' + p.handle + '">' + p.title + '</a>' +
      '<div class="cdt-pick__price">' + money(p.price) + ' ' + cmp + '</div>' +
      action +
      '</article>';
  }

  function addFromPicker(btn) {
    var id = btn.getAttribute('data-pick-add');
    if (!id) return;
    btn.disabled = true;
    var was = btn.textContent;
    btn.textContent = 'Se adaugă…';

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(id, 10), quantity: 1 })
    })
      .then(function (r) { if (!r.ok) throw new Error('add'); return r.json(); })
      .then(function () {
        btn.textContent = 'Adăugat ✓';
        /* Re-render the drawer behind the sheet so the totals and the progress
           line update immediately — the whole point of adding from here. */
        if (typeof window.cdxCartUpdated === 'function') return window.cdxCartUpdated();
      })
      .then(function () { return readCart(true); })
      .catch(function () { btn.disabled = false; btn.textContent = was; });
  }

  function openPicker(card) {
    var el = buildPicker();
    var handles = (card.getAttribute('data-handles') || '').split(',').filter(Boolean);
    var total = parseInt(card.getAttribute('data-total'), 10) || handles.length;

    el.querySelector('[data-pick-label]').textContent = card.getAttribute('data-label') || '';
    el.querySelector('[data-pick-name]').textContent = card.getAttribute('data-name') || '';

    /* Prefer the live progress line ("mai adaugă 1 produs") over the static
       rule — it is the sentence that sent them here. */
    var prog = card.querySelector('[data-cdt-voucher-progress]');
    var rule = (prog && !prog.hidden && prog.textContent.trim())
      || card.getAttribute('data-rule') || '';
    if (total > handles.length) rule += ' · ' + handles.length + ' din ' + total + ' produse';
    el.querySelector('[data-pick-rule]').textContent = rule;

    /* "See all" goes to the campaign's own collection page. The sheet shows at
       most PICK_BATCH products and the snapshot itself caps at 40, so without
       this a large campaign was silently truncated with no way through. Hidden
       when the campaign has no generated collection. */
    var all = el.querySelector('[data-pick-all]');
    var colUrl = card.getAttribute('data-collection') || '';
    if (all) {
      if (colUrl) { all.href = colUrl; all.hidden = false; }
      else { all.removeAttribute('href'); all.hidden = true; }
    }

    /* Show what is already in the basket against what the offer needs: a filled
       slot per qualifying product, an empty one for each still missing, and a
       sentence naming the discount that lands when they are full. The rule bar
       above states the condition; this states the PAYOFF, which is what makes
       someone add the extra item. Quantity offers only — "2 of 3 boxes" would
       be a lie on a min-subtotal campaign. */
    var prog = el.querySelector('[data-pick-prog]');
    var type = card.getAttribute('data-condition-type');
    var need = Math.round(parseFloat(card.getAttribute('data-condition-value')) || 0);
    if (prog && type === 'min_quantity' && need > 0) {
      var have = Math.min(need, qualifying(card, 'quantity'));
      var missing = Math.max(0, need - have);
      el.querySelector('[data-pick-slots]').innerHTML = buildSlotsHTML(card, need);
      el.querySelector('[data-pick-need]').textContent = missing === 0
        ? 'Condiție îndeplinită — reducerea ' + (card.getAttribute('data-label') || '') + ' se aplică la finalizare.'
        : 'Mai adaugă ' + missing + (missing === 1 ? ' produs' : ' produse') +
          ' și primești ' + (card.getAttribute('data-label') || 'reducerea') + '.';
      el.querySelector('[data-pick-slots]').classList.toggle('is-ready', missing === 0);
      prog.classList.toggle('is-ready', missing === 0);
      prog.hidden = false;
    } else if (prog) {
      prog.hidden = true;
    }

    var grid = el.querySelector('[data-pick-grid]');
    grid.innerHTML = '<p class="cdt-pick__loading">Se încarcă produsele…</p>';
    el.removeAttribute('hidden');
    document.body.classList.add('cdt-pick-open');

    var wanted = handles.slice(0, PICK_BATCH);
    Promise.all(wanted.map(function (h) {
      if (pickCache[h]) return Promise.resolve(pickCache[h]);
      return fetch('/products/' + encodeURIComponent(h) + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) { if (p) pickCache[h] = p; return p; })
        .catch(function () { return null; });
    })).then(function (list) {
      var ok = list.filter(Boolean);
      var campName = card.getAttribute('data-name') || '';
      grid.innerHTML = ok.length
        ? ok.map(function (prod) { return cardHTML(prod, campName); }).join('')
        : '<p class="cdt-pick__loading">Nu am putut încărca produsele din campanie.</p>';
    });
  }

  document.addEventListener('click', function (e) {
    var saveBtn = e.target.closest && e.target.closest('[data-cdt-voucher-save]');
    if (saveBtn) {
      var c1 = saveBtn.closest('[data-cdt-voucher]');
      if (c1) { save(c1.getAttribute('data-code')); paintSaveButtons(); paintCart(); }
      return;
    }
    var applyBtn = e.target.closest && e.target.closest('[data-cdt-voucher-apply]');
    if (applyBtn) {
      var c2 = applyBtn.closest('[data-cdt-voucher]');
      if (c2) { e.preventDefault(); applyCode(c2, applyBtn); }
      return;
    }
    var browseBtn = e.target.closest && e.target.closest('[data-cdt-voucher-browse]');
    if (browseBtn) {
      var c3 = browseBtn.closest('[data-cdt-voucher]');
      if (c3) { e.preventDefault(); openPicker(c3); }
    }
  });

  function start() {
    revealSaved();
    paintSaveButtons();
    if (document.querySelector('.cdt-vch-list--cart [data-cdt-voucher]')) readCart();

    /* The drawer replaces its own markup on every cart change, so re-bind state
       to the fresh nodes rather than polling. */
    if (window.MutationObserver) {
      var root = document.querySelector('[data-cdx]');
      if (root) {
        var pending = null;
        new MutationObserver(function () {
          if (painting) return;
          /* Immediately, before the debounce. paintAll() — not just
             revealSaved() — because the re-render also ships the slot row and
             the progress line hidden, and revealing only the card left it
             collapsing 151px -> 108px for a second before they came back.

             This is free: paintCart() reads the CACHED `cart` already in memory
             from the last read, so the correct state is drawn in the same tick
             as the re-render. The debounced readCart() below still refreshes
             with authoritative data straight after; it just no longer decides
             whether anything is visible.

             paintAll() sets the `painting` flag, so its own writes do not
             re-enter this observer. */
          paintAll();
          clearTimeout(pending);
          pending = setTimeout(function () { paintSaveButtons(); readCart(); }, 200);
        }).observe(root, { childList: true, subtree: true });
      }
    }
    document.addEventListener('cdt:cart-updated', readCart);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
